import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import {
  IIntInvoiceRegister,
  IIntInvoiceRemoveFromRegisterPayload,
} from '../../../../../../models/invoice/int-invoice-register';
import { collatorCompare, compareByNestedKey, compareNumbers, compareStringDates, compareStrings, compareStringWithoutRegister } from '@shared/utils/compare-functions.util';
import { formatCostForInternationalInvoice } from '@shared/utils/invoice.util';
import { EIIState, EIIPrintForm } from '@shared/enums/international-invoice.enums';
import { TableComponent } from '@shared/classes/table-component';
import { IntRegistersShowDataSource } from './int-register-show.datasource';
import { LocalStorageService } from 'ngx-webstorage';
import { IntRegistersApiService } from '../../../../../../services/int-registers-api/int-registers-api.service';
import {
  INVOICE_STATE_MAP,
} from '../../../../../../components/dashboard/invoices/components/invoices-my/invoices-my-international/invoices-my-international.constants';
import { Invoice } from '@models/invoice/invoice.model';
import { InvoiceService } from '../../../../../../services/invoices/invoice.service';
import { AlertToastService } from '@services';
import { Sort } from '@angular/material/sort';
import { InternationalInvoice } from '../../../../../../models/international-invoice';
import {
  getSubStateComment,
} from '../../../../../../components/dashboard/invoices/components/invoices-my/invoices-my-international/invoices-my-international.helpers';

@Component({
  selector: 'app-int-register-show',
  templateUrl: './int-register-show.component.html',
  styleUrls: ['./int-register-show.component.scss',
    '../../../../../../../styles/mat-override/mat-table.common.scss',
  ],
})
export class IntRegisterShowComponent extends TableComponent<InternationalInvoice, IntRegistersShowDataSource>
  implements OnInit {
  disableColumns: string[];
  displayedColumns: string[] = [
    'docNumber',
    'date',
    'CountryRecipientDescription',
    'cost',
    'price',
    'amount',
    'OrderNumber',
    'description',
    'receiver',
    'receiverPhone',
    'receiverCity',
    'address',
    'delivery',
    'status',
    'statusComment',
    'editDate',
    'options',
  ];

  dataSource: IntRegistersShowDataSource;
  @Input() register: IIntInvoiceRegister = null;
  @Output() removedFromRegister = new EventEmitter<string[]>();

  sortConf = {
    docNumber: compareByNestedKey(compareNumbers, 'Number'),
    amount: compareByNestedKey(compareNumbers, 'PlacesAmount'),
    date: compareByNestedKey(compareStringDates, 'CreationDate'),
    editDate: compareByNestedKey(compareStringDates, 'DateTime'),
    receiverPhone: compareByNestedKey(compareNumbers, 'PhoneRecipient'),
    CountryRecipientDescription: compareByNestedKey(compareStrings, 'CountryRecipientDescription'),
    receiverCity: compareByNestedKey(compareStringWithoutRegister, 'CityRecipient'),
    address: compareByNestedKey(compareStrings, 'AddressRecipient'),
    delivery: compareByNestedKey(compareStrings, 'DeliveryTypeDescription'),
    description: compareByNestedKey(compareStrings, 'CargoDescription'),
    cost: compareByNestedKey(compareNumbers, 'Cost'),
    price: compareByNestedKey(compareNumbers, 'DocumentCost'),
    status: (a: InternationalInvoice, b: InternationalInvoice, isAsc: boolean) => {
      return compareStrings(this.getStatus((a as any).State), this.getStatus((b as any).State), isAsc);
    },
    receiver: (a: InternationalInvoice, b: InternationalInvoice, isAsc: boolean) =>
      compareStrings(this.getRecipient(a), this.getRecipient(b), isAsc),
    statusComment: (a: InternationalInvoice, b: InternationalInvoice, isAsc: boolean) => compareStrings(
      this.getStatusComment(a?.State as EIIState, a?.SubStateDocument),
      this.getStatusComment(b?.State as EIIState, b?.SubStateDocument),
      isAsc,
    ),
    OrderNumber: compareByNestedKey(collatorCompare, 'OrderNumber'),
  };
  defaultSortEvent: Sort = {
    active: 'date',
    direction: 'desc',
  };
  currentSortEvent: Sort = this.defaultSortEvent;
  itemsPerPage: number = 5;

  storageKey: string;
  protected readonly formatCostForInternationalInvoice = formatCostForInternationalInvoice;
  protected readonly PRINT_FORMS = EIIPrintForm;

  constructor(
    protected storage: LocalStorageService,
    protected intRegistersApi: IntRegistersApiService,
    private invoiceService: InvoiceService,
    private alertToastService: AlertToastService,
  ) {
    super();
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.initDataSource();
  }

  initDataSource(): void {
    this.dataSource = new IntRegistersShowDataSource(this.intRegistersApi, this.alertToastService);
    this.dataSource.loadInvoices(this.register.docNumber, 1, this.itemsPerPage);
  }

  remove(invoice: Invoice): void {
    const data: IIntInvoiceRemoveFromRegisterPayload = {
      Ref: this.register.ref,
      DocumentRefs: [invoice.Ref],
    };
    this.intRegistersApi.removeFromRegister(data).subscribe(() => {
      this.update();
      this.removedFromRegister.emit([invoice.Ref]);
      this.alertToastService.pushSuccess('Посилку успішно видалено з реєстру');
    }, (errors) => {
      if (errors[0]) {
        this.alertToastService.pushApiErrors(errors);
      }
    });
  }

  isZebraPrintAvailable(state: EIIState): boolean {
    const allowed = [
      EIIState.VERIFIED,
    ];

    return allowed.includes(state);
  }

  getRecipient(invoice: InternationalInvoice): string {
    if (!!invoice?.CounterpartyTypeRecipient && invoice.CounterpartyTypeRecipient === 'Organization') {
      return invoice.CounterpartyRecipient;
    }

    return [invoice.CounterpartyRecipientFirstName, invoice.CounterpartyRecipientLastName].filter(Boolean).join(' ');
  }

  print(invoices: Invoice[], printForm: EIIPrintForm): void {
    const invoicesIds = invoices.map(i => i.Ref);
    this.invoiceService.printDocuments(invoicesIds, 'pdf', { printForm })
      .subscribe(() => {
        this.update();
      }, (errors) => {
        if (errors[0]) {
          this.alertToastService.pushApiErrors(errors);
        }
      });
  }

  update(): void {
    this.dataSource.loadInvoices(this.register.docNumber, this.paginator.pageIndex + 1, this.itemsPerPage);
  }

  updateInvoices(): void {
    this.update();
  }

  getStatus(state: EIIState): string {
    return INVOICE_STATE_MAP[state]?.status;
  }

  getStatusComment(state: EIIState, subStateDocument: string): string {
    const statusComment = INVOICE_STATE_MAP[state]?.statusComment;

    if (statusComment) {
      return statusComment;
    }

    return getSubStateComment(state, subStateDocument);
  }
}
